/**
 * Пояснение — значок «i» рядом с подписью, текст по тапу во всплывающей
 * подсказке.
 *
 * Раньше пояснения стояли серым текстом под каждым полем, и форма из шести
 * полей читалась как инструкция: «Это основной долг из справки банка…»,
 * «Дата выдачи нужна, чтобы…». Человеку нужно заполнить поле, а не прочитать
 * абзац. Теперь на экране — подпись и значок, а объяснение — для того, кто
 * спросил. Предупреждения и ошибки сюда не прячутся: их надо видеть сразу.
 *
 * Значок — `span`, а не `button`: подсказка стоит внутри `<label>`, а кнопка
 * внутри него стала бы тем, что подпись подписывает, — тап по подписи
 * перестал бы ставить курсор в поле. Тап по значку подпись не трогает.
 *
 * Текст рисуется в `body` и только пока открыт: внутри подписи он попал бы в
 * её текст, и поле называлось бы абзацем.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icon.js';

/** Отступ подсказки от краёв экрана и от значка. */
const EDGE = 12;
const GAP = 6;

export function Tip({ children, label = 'Пояснение', size = 12, className = 'tipbtn', glyph }: {
  children: ReactNode;
  /** Для экранного диктора: что объясняет значок. */
  label?: string;
  /** Размер значка: 12 — не крупнее подписи рядом; в составе строк расчёта мельче. */
  size?: number;
  /** Свой облик значка — например, значок связи в шапке главной. */
  className?: string;
  /** Свой рисунок вместо «i». */
  glyph?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const icon = useRef<HTMLSpanElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const id = useId();

  /*
   * Подсказка под значком, а если снизу не помещается — над ним. По
   * горизонтали — от значка, но не за край экрана.
   */
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const a = icon.current?.getBoundingClientRect();
    const b = box.current?.getBoundingClientRect();
    if (!a || !b) return;
    const vw = window.innerWidth;
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const left = Math.min(Math.max(EDGE, a.left + a.width / 2 - 24), vw - EDGE - b.width);
    const below = a.bottom + GAP;
    const top = below + b.height <= vh - EDGE ? below : Math.max(EDGE, a.top - GAP - b.height);
    setPos({ left, top });
  }, [open]);

  /* Закрывается тапом мимо, прокруткой и Escape. */
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const outside = (e: PointerEvent) => {
      const t = e.target as Node;
      if (icon.current?.contains(t) || box.current?.contains(t)) return;
      close();
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
    document.addEventListener('pointerdown', outside, true);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', key, true);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', key, true);
    };
  }, [open]);

  const toggle = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    // Значок стоит в подписи поля или в строке-кнопке: тап по нему не должен
    // ни ставить курсор в поле, ни открывать строку.
    e.preventDefault();
    e.stopPropagation();
    setOpen((o) => !o);
  };

  return (
    <>
      <span ref={icon} className={`${className}${open ? ' on' : ''}`} role="button" tabIndex={0}
        aria-label={label} aria-expanded={open} {...(open ? { 'aria-describedby': id } : {})}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(e); }}>
        {glyph ?? <Icon name="circle-info" size={size} />}
      </span>
      {open && typeof document !== 'undefined' && createPortal(
        <div ref={box} id={id} role="tooltip" className="tipbox"
          style={pos ? { left: pos.left, top: pos.top } : { visibility: 'hidden', left: 0, top: 0 }}>
          {children}
        </div>,
        document.body,
      )}
    </>
  );
}
